import axios from 'axios';

// Get API base URL from environment or default
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth tokens (if needed in future)
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const auth = localStorage.getItem('vodafone_auth');
    if (auth) {
      const { user } = JSON.parse(auth);
      config.headers.Authorization = `Bearer ${user.token || 'demo-token'}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('vodafone_auth');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Methods
export const apiService = {
  // Meeting and Agenda Selection
  getMeetings: () => api.get('/meetings'),
  getMeetingAgendas: (meeting) => api.get(`/meetings/${encodeURIComponent(meeting)}/agendas`),
  
  // Dashboard Data
  getDashboardData: (meeting, agenda) => api.get('/dashboard', { params: { meeting, agenda } }),
  getDashboardCharts: (meeting, agenda) => api.get('/dashboard/charts', { params: { meeting, agenda } }),
  getExecutiveSummary: () => api.get('/dashboard/summary'),
  
  // Subtopics
  getSubtopics: (meeting, agenda) => api.get('/subtopics', { params: { meeting, agenda } }),
  getSubtopicClusters: (subtopicId) => api.get(`/subtopics/${subtopicId}/clusters`),
  getSubtopicProposalsByDocument: (subtopicId) => api.get(`/subtopics/${subtopicId}/proposals-by-document`),
  
  // Proposals
  getCommonProposals: () => api.get('/proposals/common'),
  getRareProposals: () => api.get('/proposals/rare'),
  getProposalMatrix: () => api.get('/proposals/matrix'),
  
  // Cross-references (legacy)
  getCrossReferences: () => api.get('/cross-references'),
  
  // New Cross-reference Analysis
  getCrossReferenceConclusions: (params = {}) => api.get('/cross-reference-conclusions', { params }),
  getCrossReferenceBySubtopic: () => api.get('/cross-reference-by-subtopic'),
  getCrossReferenceMetadata: () => api.get('/cross-reference-analysis-metadata'),
  
  // Advanced Cross-reference Table APIs
  getCrossReferenceConclusionsTable: (meeting, agenda, params = {}) => 
    api.get('/cross-reference-conclusions-table', { params: { meeting, agenda, ...params } }),
  getCrossReferenceProposalsTable: (meeting, agenda, params = {}) => 
    api.get('/cross-reference-proposals-table', { params: { meeting, agenda, ...params } }),
  getCrossReferenceSubtopics: (meeting, agenda) => 
    api.get('/cross-reference-subtopics', { params: { meeting, agenda } }),
  getCrossReferenceDocumentIds: (meeting, agenda) => 
    api.get('/cross-reference-document-ids', { params: { meeting, agenda } }),
  
  // Cluster Details
  getClusterDetails: (clusterId) => api.get(`/clusters/${clusterId}`),
  
  // Legacy endpoints (for backward compatibility)
  getTdocs: () => api.get('/tdocs/'),
  getTdocById: (id) => api.get(`/tdocs/${id}`),
  getProposals: () => api.get('/proposals/'),
  getProposalById: (id) => api.get(`/proposals/${id}`),
  
  // Analysis Pipeline (admin only)
  runAnalysis: () => api.post('/analysis/run'),
  
  // AI Summary
  getAISummary: (subtopicId) => api.get(`/subtopics/${subtopicId}/ai-summary`),
  generateAISummary: (subtopicId, forceRegenerate = false) => api.post(`/subtopics/${subtopicId}/generate-summary`),
  
  // Overall Executive Summary
  generateOverallExecutiveSummary: (subtopicSummaries) => api.post('/generate-overall-executive-summary', { subtopic_summaries: subtopicSummaries }),
  
  // GPT-4o Semantic Analysis
  getSemanticAnalysis: (meeting, agenda) => api.get('/semantic-analysis', { params: { meeting, agenda } }),
  
  // Documents
  getDocuments: (meeting, agenda) => api.get('/documents', { params: { meeting, agenda } }),
  
  // Company Analytics
  getCompanyAnalytics: (meeting, agenda) => api.get('/company-analytics', { params: { meeting, agenda } }),
  
  // Study Item Context
  getStudyItemContext: async (meetingId = 'RAN2%23128', agendaItem = '8.2.3') => {
    try {
      const response = await api.get(`/study-item-context?meeting_id=${meetingId}&agenda_item=${agendaItem}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching study item context:', error);
      throw error;
    }
  },
  
  extractStudyContext: async (meetingId = 'RAN2%23128', agendaItem = '8.2.3', forceRefresh = false) => {
    try {
      const response = await api.post('/study-item-context/extract', null, {
        params: {
          meeting_id: meetingId,
          agenda_item: agendaItem,
          force_refresh: forceRefresh
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error extracting study context:', error);
      throw error;
    }
  },
  
  getStudyProgress: async (meetingId = 'RAN2%23128', agendaItem = '8.2.3') => {
    try {
      const response = await api.get(`/study-item-context/progress?meeting_id=${meetingId}&agenda_item=${agendaItem}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching study progress:', error);
      throw error;
    }
  },

  getAgendaContext: async (meeting, agenda) => {
    try {
      const response = await api.get(`/agenda-context?meeting=${encodeURIComponent(meeting)}&agenda=${encodeURIComponent(agenda)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching agenda context:', error);
      throw error;
    }
  },
};

// Error handler utility
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    
    // Handle Pydantic validation errors (array of error objects)
    if (Array.isArray(detail)) {
      return detail.map(err => err.msg || err.type || 'Validation error').join(', ');
    }
    
    // Handle string details
    if (typeof detail === 'string') {
      return detail;
    }
    
    // Handle object details
    if (typeof detail === 'object' && detail.msg) {
      return detail.msg;
    }
    
    return 'Invalid request format';
  } else if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else {
    return defaultMessage;
  }
};

// Loading states utility
export const createLoadingState = () => ({
  loading: false,
  error: null,
  data: null,
});

// Convenience functions for meeting/agenda selection
export const getMeetings = async () => {
  try {
    const response = await apiService.getMeetings();
    return response.data;
  } catch (error) {
    console.error('Error fetching meetings:', error);
    throw error;
  }
};

export const getMeetingAgendas = async (meeting) => {
  try {
    const response = await apiService.getMeetingAgendas(meeting);
    return response.data;
  } catch (error) {
    console.error('Error fetching agendas:', error);
    throw error;
  }
};

export default api; 